import styled, { css } from 'styled-components';
import {
  v2Colors, v2Breakpoints, v2FontFamily, v2ReadableFontFamily,
} from './tokens';

const selectArrow = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='5' viewBox='0 0 7 5'%3E%3Cpath d='M1 1l2.5 2.5L6 1' stroke='%2398a2b3' stroke-width='1.2' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";

// Outlined danger exclamation-circle, for the invalid state of plain
// text/number fields and (inboard of the arrow) <select> and react-select.
// NOT applied to <input type="date"> — its native picker indicator owns
// that edge.
export const warningIcon = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='%23d92d20' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='8' cy='8' r='6.5'/%3E%3Cline x1='8' y1='4.5' x2='8' y2='8.5'/%3E%3Cline x1='8' y1='11' x2='8' y2='11'/%3E%3C/svg%3E\")";

export const inputFieldStyles = css`
  font-family: ${v2FontFamily};

  .form-control {
    width: 100%;
    min-height: 40px;
    /* The legacy global input.css sets a stray margin-bottom: 10px on any
       <input class="form-control"> (shared class name with Bootstrap) —
       neutralize it here without touching that shared file. */
    margin-bottom: 0;
    padding: 8px 16px;
    font-family: ${v2ReadableFontFamily};
    font-size: 15px;
    font-weight: 400;
    color: ${v2Colors.text};
    background-color: ${v2Colors.inputBackground};
    border: 1.5px solid ${v2Colors.border};
    border-radius: 12px;
    box-shadow: none;
    transition: border-color 120ms ease, box-shadow 120ms ease;

    @media (max-width: ${v2Breakpoints.tablet}) {
      font-size: 16px;
    }

    &::placeholder {
      color: ${v2Colors.textFaint};
      font-family: inherit;
      font-size: 15px;
      font-weight: 600;
    }

    &:hover {
      border-color: ${v2Colors.borderStrong};
    }

    /* Neutral focus ring — deliberately NOT the accent green, which is
       reserved for the confirmed/valid state (.is-valid below). A green
       focus border on an untouched required <select> reads as "already
       validated" the moment it is opened and closed. */
    &:focus {
      outline: none;
      border-color: ${v2Colors.borderStrong};
      box-shadow: 0 0 0 3px rgba(52, 64, 84, 0.14);
    }

    &:disabled {
      background-color: ${v2Colors.disabledSurface};
      border-color: ${v2Colors.disabledBorder};
      color: ${v2Colors.textMuted};
      cursor: not-allowed;
    }

    /* Bootstrap's .form-control.is-invalid / .is-valid inject a right-aligned
       icon (background-image) + extra padding-right at this same specificity;
       that icon collides with the <select> arrow and the native
       <input type="date"> picker indicator. Own both states explicitly. */
    &.is-invalid {
      border-color: ${v2Colors.danger};
      box-shadow: 0 0 0 3px rgba(217, 45, 32, 0.12);
      padding-right: 40px;
      background-image: ${warningIcon};
      background-repeat: no-repeat;
      background-position: right 12px center;
      background-size: 16px 16px;
    }

    &.is-invalid:focus {
      box-shadow: 0 0 0 4px rgba(217, 45, 32, 0.18);
    }

    /* Success: a green border + a soft green halo (same idea as the focus
       ring) — reads as confirmed without a thick slab border or a check icon. */
    &.is-valid {
      border-color: ${v2Colors.primary};
      box-shadow: 0 0 0 3px rgba(0, 166, 81, 0.12);
      padding-right: 16px;
      background-image: none;
    }
  }

  select.form-control {
    appearance: none;
    -webkit-appearance: none;
    background-image: ${selectArrow};
    background-repeat: no-repeat;
    background-position: right 13px center;
    /* Pin the arrow size in every state — Bootstrap's global
       .form-control.is-valid / .is-invalid otherwise leaks a ~17px
       background-size that scales this chevron up only once validated. */
    background-size: 12px 9px;
    padding-right: 28px;
    text-align: left;
    cursor: pointer;

    /* Success stays quiet: just the arrow, no icon. */
    &.is-valid {
      background-image: ${selectArrow};
      background-size: 12px 9px;
      padding-right: 28px;
    }

    /* Invalid: warning icon takes the far-right slot (same spot as on plain
       text/number fields); the arrow shifts inboard, left of it. */
    &.is-invalid {
      background-image: ${warningIcon}, ${selectArrow};
      background-repeat: no-repeat, no-repeat;
      background-position: right 12px center, right 32px center;
      background-size: 16px 16px, 12px 9px;
      padding-right: 50px;
    }
  }

  /* Native date input: pin the calendar picker indicator to the right edge
     (so its position doesn't drift with padding), which frees room to show
     the same warning sign as other fields just inboard of it. */
  input[type='date'].form-control {
    position: relative;
    padding-right: 40px;
  }

  input[type='date'].form-control::-webkit-calendar-picker-indicator {
    position: absolute;
    right: 14px;
    top: 50%;
    margin: 0;
    transform: translateY(-50%);
    cursor: pointer;
  }

  /* Success stays quiet — no icon (base rule already clears background-image). */

  /* Invalid: warning icon takes the far-right slot (same spot as every other
     field type); the calendar picker shifts inboard, left of it. */
  input[type='date'].form-control.is-invalid::-webkit-calendar-picker-indicator {
    right: 34px;
  }

  input[type='date'].form-control.is-invalid {
    background-image: ${warningIcon};
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 16px 16px;
    padding-right: 54px;
  }

  .invalid-feedback {
    display: block;
    margin-top: 3px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    color: ${v2Colors.danger};
    text-align: left;

    &:empty {
      display: none;
    }
  }

  .form-input-hint,
  .sub-text {
    font-size: 13px;
    color: ${v2Colors.textMuted};
  }

  /* Bootstrap's default .form-text (used for FormInputText's phone-number
     helper) is a plain, easy-to-miss caption — bold + dark ink (same
     register as the "Comune: ..." confirmation line under the address
     field) reads as evident without adding another color to the page. */
  .form-text {
    margin-top: 4px;
    font-size: 12px;
    font-weight: 700;
    color: ${v2Colors.text};
  }
`;

export const Label = styled.label`
  display: block;
  width: 100%;
  margin-bottom: 5px;
  font-family: ${v2FontFamily};
  font-size: 13.5px;
  font-weight: 700;
  letter-spacing: 0.1px;
  text-align: left;
  color: ${v2Colors.textMuted};
`;

export const Paragraph = styled.div`
  margin-bottom: 3px;
  font-family: ${v2FontFamily};
  font-size: 13px;
  text-align: left;
  color: ${v2Colors.textFaint};
`;

export const FormGroup = styled.div`
  margin: 5px 0;
  ${inputFieldStyles}
`;

export const FormGroupAddress = styled.div`
  margin-top: 6px;
  ${inputFieldStyles}

  .form-control {
    margin-bottom: 0;
  }
`;
